import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ChangePasswordRequired from './pages/ChangePasswordRequired';
import TwoFactorSetupRequired from './pages/TwoFactorSetupRequired';
import PasskeySetupRequired from './pages/PasskeySetupRequired';
import TelegramLinkRequired from './pages/TelegramLinkRequired';
import SecondAdminRequired from './pages/SecondAdminRequired';
// All page/component stylesheets stay global in main.css (see styles/pageStyles.js).
import './styles/pageStyles';
import { AddressLabelsProvider } from './components/AddressLabel';
import ToastNotifications from './components/ToastNotifications';
import { getCurrentUser, setActingUserId } from './services/api';
import { initPush } from './utils/push';
import PushPrompt from './components/PushPrompt';
import TwoFactorPromptModal from './components/TwoFactorPromptModal';
import PasskeyPromptModal from './components/PasskeyPromptModal';
import UpdateBanner from './components/UpdateBanner';
import AiChatBubble from './components/AiChatBubble';
import { lazyWithReload } from './utils/lazyWithReload';
import './App.css';

const UserManagement = lazyWithReload(() => import('./pages/UserManagement'));
const MoneyAvailable = lazyWithReload(() => import('./pages/MoneyAvailable'));
const CustomerManagement = lazyWithReload(() => import('./pages/CustomerManagement'));
const CryptoManagement = lazyWithReload(() => import('./pages/CryptoManagement'));
const BlockchainMonitor = lazyWithReload(() => import('./pages/BlockchainMonitor'));
const CreateOrder = lazyWithReload(() => import('./pages/CreateOrder'));
const OrderHistory = lazyWithReload(() => import('./pages/OrderHistory'));
const TaskTracking = lazyWithReload(() => import('./pages/TaskTracking'));
const TransactionHistory = lazyWithReload(() => import('./pages/TransactionHistory'));
const ServerLogs = lazyWithReload(() => import('./pages/ServerLogs'));
const ServerLogsAdvanced = lazyWithReload(() => import('./pages/ServerLogsAdvanced'));
const Expenses = lazyWithReload(() => import('./pages/Expenses'));
const PlatformManagement = lazyWithReload(() => import('./pages/PlatformManagement'));
const Checkpoints = lazyWithReload(() => import('./pages/Checkpoints'));
const Reports = lazyWithReload(() => import('./pages/Reports'));
const MyPerformance = lazyWithReload(() => import('./pages/MyPerformance'));
const AiAssistantAdmin = lazyWithReload(() => import('./pages/AiAssistantAdmin'));
const AiStaff = lazyWithReload(() => import('./pages/AiStaff'));

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Numbers are typed, never nudged (operator 2026-10-10): the spinner is hidden
  // in index.css; this stops the other accidental tweak — a mouse wheel or
  // trackpad scroll over a FOCUSED number field changes its value in every
  // browser. Blurring on wheel keeps the page scrolling and the value intact.
  useEffect(() => {
    const onWheel = () => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number') el.blur();
    };
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  // User identities (2026-10-30): every request names the role account this
  // page was drawn for, so the server can refuse a stale tab after a role switch.
  useEffect(() => {
    setActingUserId(user?.id);
  }, [user]);

  // Another tab switched role (RoleSwitcher writes this key): reload so this
  // tab is drawn for the new role too.
  useEffect(() => {
    const onStorage = (e) => { if (e.key === 'crm-role-switch') window.location.reload(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Session heartbeat: while logged in, ping /me every 60s so an idle or
  // backgrounded tab still notices a cleared session. A 401 here flows
  // through the api interceptor's handle401 (re-login notification + redirect)
  // automatically. It also keeps the forced-password-change flag in step with
  // the server (operator rule 2026-10-12): a tab that was already open when the
  // flag was set is pulled into the forced page without a manual reload.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      getCurrentUser()
        .then((u) => {
          // The cookie is shared by every tab: if another tab switched role (or
          // signed in as someone else) this page is stale — reload it.
          if (u && u.id !== user.id) { window.location.reload(); return; }
          if (u && (u.role !== user.role
            || JSON.stringify(u.roles) !== JSON.stringify(user.roles)
            || !!u.mustChangePassword !== !!user.mustChangePassword
            || !!u.twoFactorSetupRequired !== !!user.twoFactorSetupRequired
            || !!u.twoFactorEnabled !== !!user.twoFactorEnabled
            || !!u.passkeySetupRequired !== !!user.passkeySetupRequired
            || !!u.passkeyEnabled !== !!user.passkeyEnabled
            || !!u.telegramLinkRequired !== !!user.telegramLinkRequired
            || !!u.secondAdminRequired !== !!user.secondAdminRequired)) setUser(u);
        })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Register the SW + (re)subscribe silently whenever someone is signed in —
  // after the session check AND after a fresh login. Until 2026-10-09 this ran
  // only from checkAuth, so a device that had to log in again (the cedge server
  // move reset every session) never registered until its next cold start and
  // got no alerts. The permission PROMPT still comes from a click in
  // <PushPrompt> (browsers suppress an auto-requested prompt).
  // Not while a forced password change or the compulsory 2FA setup is pending
  // (the server refuses the subscribe call until then).
  useEffect(() => {
    if (user && !user.mustChangePassword && !user.twoFactorSetupRequired && !user.passkeySetupRequired && !user.secondAdminRequired) initPush(); // fire-and-forget, best-effort
  }, [user]);

  const checkAuth = async () => {
    try {
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* key by user so the label cache refetches on login/logout */}
      <AddressLabelsProvider key={user?.id || 'anon'}>
      <div className="App">
        {/* THE toast host. Every showSuccess/showError call in the app rendered
            NOTHING until this was mounted (react-hot-toast needs exactly one
            <Toaster/>; chipped 2026-08-01, mounted 2026-08-08) — failed claims,
            releases, reverts and approval decisions all failed silently. One
            instance for the whole app, above the routes like UpdateBanner. */}
        <ToastNotifications />
        {user && !user.mustChangePassword && !user.twoFactorSetupRequired && !user.passkeySetupRequired && !user.telegramLinkRequired && !user.secondAdminRequired && <PushPrompt />}
        {/* Step-up: a protected action asks for a fresh authenticator code here. */}
        <TwoFactorPromptModal />
        {/* Step-up (admins): user-management actions ask for a passkey here. */}
        <PasskeyPromptModal />
        {/* Deploy-version beacon: one instance for the whole app (all routes,
            including /login) so stale tabs learn about new builds. */}
        <UpdateBanner />
        {/* AI assistant bubble (docs/ai-module-design-2026-10-30.md §8.2): only
            once every compulsory gate is cleared — the server refuses /api/ai
            until then — and it hides itself unless /api/ai/status says it is on
            for this role. Inside <Router>: its links navigate in-app. */}
        {user && !user.mustChangePassword && !user.twoFactorSetupRequired && !user.passkeySetupRequired && !user.telegramLinkRequired && !user.secondAdminRequired && <AiChatBubble />}
        <Suspense fallback={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            Loading...
          </div>
        }>
        {/* Operator rule 2026-10-12: while the server flags this account as
            "must change password", the forced page is the ONLY thing rendered
            — whatever URL was asked for — until the new password is saved. */}
        {user && user.mustChangePassword ? (
        <Routes>
          <Route path="*" element={<ChangePasswordRequired user={user} setUser={setUser} />} />
        </Routes>
        ) : user && user.twoFactorSetupRequired ? (
        /* Compulsory 2FA setup (after any forced password change): the ONLY
           page until the authenticator is confirmed; the server also refuses
           every other API call with 403 TWO_FACTOR_SETUP_REQUIRED. */
        <Routes>
          <Route path="*" element={<TwoFactorSetupRequired user={user} setUser={setUser} />} />
        </Routes>
        ) : user && user.passkeySetupRequired ? (
        /* Compulsory passkey setup for administrators (after the two gates
           above): the ONLY page until a passkey is registered; the server
           refuses every other API call with 403 PASSKEY_SETUP_REQUIRED. */
        <Routes>
          <Route path="*" element={<PasskeySetupRequired user={user} setUser={setUser} />} />
        </Routes>
        ) : user && user.telegramLinkRequired ? (
        /* Compulsory Telegram link (2026-10-30; after the gates above, before
           the second-admin gate): the ONLY page until the person's Telegram is
           linked; the server refuses every other API call with
           403 TELEGRAM_LINK_REQUIRED. */
        <Routes>
          <Route path="*" element={<TelegramLinkRequired user={user} setUser={setUser} />} />
        </Routes>
        ) : user && user.secondAdminRequired ? (
        /* The CRM must always have ≥2 administrators (2026-10-24): a lone admin
           can only create the second one; the server refuses the rest with
           403 SECOND_ADMIN_REQUIRED. */
        <Routes>
          <Route path="*" element={<SecondAdminRequired user={user} setUser={setUser} />} />
        </Routes>
        ) : (
        <Routes>
          <Route 
            path="/login" 
            element={user ? <Navigate to="/dashboard" /> : <Login setUser={setUser} />} 
          />
          <Route 
            path="/dashboard" 
            element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/users" 
            element={
              user && user.role === 'administrator' ? 
              <UserManagement user={user} setUser={setUser} /> : 
              <Navigate to="/dashboard" />
            } 
          />
          <Route 
            path="/money" 
            element={
              user ? 
              <MoneyAvailable user={user} setUser={setUser} /> : 
              <Navigate to="/login" />
            } 
          />
          <Route 
            path="/customers" 
            element={
              user && (user.role === 'sales' || user.role === 'finance' || user.role === 'administrator' || user.role === 'auditor') ? 
              <CustomerManagement user={user} setUser={setUser} /> : 
              <Navigate to="/dashboard" />
            } 
          />
          <Route 
            path="/crypto" 
            element={
              user ? 
              <CryptoManagement user={user} setUser={setUser} /> : 
              <Navigate to="/login" />
            } 
          />
          <Route 
            path="/blockchain" 
            element={
              user ? 
              <BlockchainMonitor user={user} setUser={setUser} /> : 
              <Navigate to="/login" />
            } 
          />
          <Route 
            path="/create-order" 
            element={
              user && user.role === 'sales' ?
              <CreateOrder user={user} setUser={setUser} /> :
              <Navigate to="/dashboard" />
            } 
          />
          <Route 
            path="/order-history" 
            element={
              user && (user.role === 'sales' || user.role === 'finance' || user.role === 'administrator' || user.role === 'auditor') ? 
              <OrderHistory user={user} setUser={setUser} /> : 
              <Navigate to="/dashboard" />
            } 
          />
          <Route 
            path="/task-tracking" 
            element={
              user && (user.role === 'sales' || user.role === 'finance' || user.role === 'administrator' || user.role === 'auditor') ? 
              <TaskTracking user={user} setUser={setUser} /> : 
              <Navigate to="/dashboard" />
            } 
          />
          <Route 
            path="/transaction-history" 
            element={
              user ? 
              <TransactionHistory user={user} setUser={setUser} /> : 
              <Navigate to="/login" />
            } 
          />
          <Route 
            path="/server-logs" 
            element={
              user && user.role === 'administrator' ? 
              <ServerLogs user={user} setUser={setUser} /> : 
              <Navigate to="/dashboard" />
            } 
          />
          <Route 
            path="/server-logs/advanced" 
            element={
              user && user.role === 'administrator' ? 
              <ServerLogsAdvanced user={user} setUser={setUser} /> : 
              <Navigate to="/dashboard" />
            } 
          />
          <Route 
            path="/expenses" 
            element={
              user ? 
              <Expenses user={user} setUser={setUser} /> : 
              <Navigate to="/login" />
            } 
          />
          <Route 
            path="/platform" 
            element={
              user ? 
              <PlatformManagement user={user} setUser={setUser} /> : 
              <Navigate to="/login" />
            } 
          />
          <Route
            path="/checkpoints"
            element={
              user && ['administrator', 'finance', 'auditor'].includes(user.role) ?
              <Checkpoints user={user} setUser={setUser} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/reports"
            element={
              user && ['administrator', 'finance', 'auditor'].includes(user.role) ?
              <Reports user={user} setUser={setUser} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/my-performance"
            element={
              user && user.role === 'sales' ?
              <MyPerformance user={user} setUser={setUser} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/ai-assistant"
            element={
              user && user.role === 'administrator' ?
              <AiAssistantAdmin user={user} setUser={setUser} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/ai-staff"
            element={
              user && user.role === 'administrator' ?
              <AiStaff user={user} setUser={setUser} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" />} />
          {/* Catch-all: any unknown URL (e.g. bookmarked /claim-payment after
              removal) falls back to the dashboard instead of rendering blank. */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        )}
        </Suspense>
      </div>
      </AddressLabelsProvider>
    </Router>
  );
}

export default App;
